import React, { useState, useEffect } from "react";
import {
  Download,
  FileText,
  Trash2,
  RefreshCw,
  Calendar,
  User,
  Mail,
  Filter,
  Search,
  TrendingUp,
  BarChart3,
  FileDown,
} from "lucide-react";
import { resourceDownloadService } from "../services/apiService";

const BRAND_COLOR = "#166534";

const ResourceDownloads = () => {
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    resourceTitle: "",
    email: "",
    startDate: "",
    endDate: "",
    sortBy: "downloadedAt",
    sortOrder: "desc",
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0,
    hasNext: false,
    hasPrev: false,
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Fetch downloads with filters and pagination
  const fetchDownloads = async (page = 1, newFilters = filters) => {
    setLoading(true);
    setError("");
    try {
      const response = await resourceDownloadService.getDownloads({
        page,
        limit: 20,
        ...newFilters,
      });
      setDownloads(response.downloads || []);
      setPagination(response.pagination || {
        current: 1,
        pages: 1,
        total: 0,
        hasNext: false,
        hasPrev: false,
      });
    } catch (err) {
      setError(err.message || "Unable to load downloads at this time.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await resourceDownloadService.getStats();
      setStats(response);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  useEffect(() => {
    fetchDownloads();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter change
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    fetchDownloads(1, newFilters);
  };

  // Delete download record
  const handleDelete = async (downloadId) => {
    if (!window.confirm("Are you sure you want to delete this download record?")) return;
    setActionLoading(true);
    try {
      await resourceDownloadService.deleteDownload(downloadId);
      await fetchDownloads(pagination.current);
      await fetchStats();
    } catch (err) {
      setError(err.message || "An error occurred while deleting the record");
    } finally {
      setActionLoading(false);
    }
  };

  // Export to CSV
  const handleExport = async () => {
    setActionLoading(true);
    try {
      await resourceDownloadService.exportDownloads(filters);
    } catch (err) {
      setError(err.message || "An error occurred while exporting data");
    } finally {
      setActionLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Refresh data
  const handleRefresh = () => {
    fetchDownloads(pagination.current);
    fetchStats();
  };

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold" style={{ color: BRAND_COLOR }}>
          Resource Downloads
        </h1>
        <p className="text-gray-600 mt-1">
          Track and manage resource download activities
        </p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Downloads</p>
                <p className="text-2xl font-bold mt-1" style={{ color: BRAND_COLOR }}>
                  {stats.totalDownloads || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${BRAND_COLOR}15` }}>
                <Download className="w-6 h-6" style={{ color: BRAND_COLOR }} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Unique Users</p>
                <p className="text-2xl font-bold mt-1" style={{ color: BRAND_COLOR }}>
                  {stats.uniqueEmails || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${BRAND_COLOR}15` }}>
                <User className="w-6 h-6" style={{ color: BRAND_COLOR }} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Downloads Today</p>
                <p className="text-2xl font-bold mt-1" style={{ color: BRAND_COLOR }}>
                  {stats.downloadsToday || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${BRAND_COLOR}15` }}>
                <TrendingUp className="w-6 h-6" style={{ color: BRAND_COLOR }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Resources */}
      {stats && stats.topResources && stats.topResources.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center mb-4">
            <BarChart3 className="w-5 h-5 mr-2" style={{ color: BRAND_COLOR }} />
            <h2 className="text-lg font-semibold">Top Downloaded Resources</h2>
          </div>
          <div className="space-y-3">
            {stats.topResources.slice(0, 5).map((resource, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <div className="flex items-center flex-1">
                  <FileText className="w-4 h-4 mr-3 text-gray-500" />
                  <span className="text-sm font-medium truncate">{resource._id}</span>
                </div>
                <span className="text-sm font-bold ml-4" style={{ color: BRAND_COLOR }}>
                  {resource.count} downloads
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Bar */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Filter className="w-4 h-4 mr-2" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
            
            <button
              onClick={handleExport}
              disabled={actionLoading}
              className="flex items-center px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
              style={{ backgroundColor: BRAND_COLOR }}
            >
              <FileDown className="w-4 h-4 mr-2" />
              Export CSV
            </button>
            
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={filters.email}
                  onChange={(e) => handleFilterChange('email', e.target.value)}
                  placeholder="user@example.com"
                  className="w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
                  style={{ focusRing: BRAND_COLOR }}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Resource Title
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={filters.resourceTitle}
                  onChange={(e) => handleFilterChange('resourceTitle', e.target.value)}
                  placeholder="Search resource..."
                  className="w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
                  style={{ focusRing: BRAND_COLOR }}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
                style={{ focusRing: BRAND_COLOR }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
                style={{ focusRing: BRAND_COLOR }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Downloads List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">Loading downloads...</p>
          </div>
        ) : downloads.length === 0 ? (
          <div className="p-12 text-center">
            <Download className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">No downloads found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Resource
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Downloaded At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {downloads.map((download) => (
                    <tr key={download._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${BRAND_COLOR}15` }}>
                            <User className="w-5 h-5" style={{ color: BRAND_COLOR }} />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {download.name || 'Anonymous'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {download.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {download.resourceTitle}
                        </div>
                        {download.resourceId && (
                          <div className="text-xs text-gray-500">
                            ID: {download.resourceId}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {download.resourceCategory || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                          {formatDate(download.downloadedAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleDelete(download._id)}
                          disabled={actionLoading}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="px-6 py-4 border-t flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing page {pagination.current} of {pagination.pages} ({pagination.total} total)
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => fetchDownloads(pagination.current - 1)}
                    disabled={!pagination.hasPrev || loading}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => fetchDownloads(pagination.current + 1)}
                    disabled={!pagination.hasNext || loading}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ResourceDownloads;
